import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';

const SurveyResponses = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null); // eslint-disable-line no-unused-vars
  const [showAnalytics, setShowAnalytics] = useState(false); // eslint-disable-line no-unused-vars

  const firestore = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    const fetchSurveyAndResponses = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError('Authentication required');
          return;
        }

        // Fetch survey
        const surveyRef = doc(firestore, 'surveys', surveyId);
        const surveySnap = await getDoc(surveyRef);

        if (!surveySnap.exists()) {
          setError('Survey not found');
          return;
        }

        const surveyData = surveySnap.data();
        
        // Check if user owns this survey
        if (surveyData.userId !== currentUser.uid) {
          setError('You do not have permission to view this survey');
          return;
        }

        setSurvey({ id: surveySnap.id, ...surveyData });

        // Fetch responses
        const responsesQuery = query(
          collection(firestore, 'surveyResponses'),
          where('surveyId', '==', surveyId),
          orderBy('submittedAt', 'desc')
        );
        
        const responsesSnap = await getDocs(responsesQuery);
        const responsesData = responsesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setResponses(responsesData);
      } catch (err) {
        console.error('Error fetching survey data:', err);
        setError('Failed to load survey data');
      } finally {
        setIsLoading(false);
      }
    };

    if (surveyId) {
      fetchSurveyAndResponses();
    }
  }, [surveyId, firestore, auth]);

  const generateAnalytics = () => {
    if (!survey || !responses.length) return null;

    const analytics = {};

    survey.questions.forEach(question => {
      const questionResponses = responses.map(r => r.responses[question.id]).filter(Boolean);
      
      analytics[question.id] = {
        question: question.text,
        type: question.type,
        totalResponses: questionResponses.length,
        responseRate: ((questionResponses.length / responses.length) * 100).toFixed(1)
      };

      if (question.type === 'rating') {
        const ratings = questionResponses.map(r => parseInt(r)).filter(r => !isNaN(r));
        analytics[question.id].averageRating = ratings.length ? 
          (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : 0;
        analytics[question.id].ratingDistribution = {
          1: ratings.filter(r => r === 1).length,
          2: ratings.filter(r => r === 2).length,
          3: ratings.filter(r => r === 3).length,
          4: ratings.filter(r => r === 4).length,
          5: ratings.filter(r => r === 5).length,
        };
      } else if (question.type === 'yes_no') {
        const yesCount = questionResponses.filter(r => r.toLowerCase() === 'yes').length;
        const noCount = questionResponses.filter(r => r.toLowerCase() === 'no').length;
        analytics[question.id].yesPercentage = questionResponses.length ? 
          ((yesCount / questionResponses.length) * 100).toFixed(1) : 0;
        analytics[question.id].distribution = { yes: yesCount, no: noCount };
      } else if (question.type === 'multiple_choice') {
        const distribution = {};
        questionResponses.forEach(response => {
          distribution[response] = (distribution[response] || 0) + 1;
        });
        analytics[question.id].distribution = distribution;
      }
    });

    return analytics;
  };

  const analytics = generateAnalytics(); // eslint-disable-line no-unused-vars

  // eslint-disable-next-line no-unused-vars
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const copyShareLink = () => { // eslint-disable-line no-unused-vars
    const shareLink = `${window.location.origin}/survey-response/${surveyId}`;
    navigator.clipboard.writeText(shareLink).then(() => {
      toast.success('Survey link copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-black">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand" />
          <p className="text-sm text-gray-600 dark:text-[#666666]">Loading survey responses...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-black p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white dark:bg-black rounded-2xl p-6 text-center border border-gray-200 dark:border-[#1A1A1A] shadow-sm"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">Error</h1>
          <p className="text-base text-gray-600 dark:text-[#666666] mb-6 leading-relaxed">
            {error}
          </p>
          <motion.button
            onClick={() => navigate('/app')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-3 px-6 bg-brand hover:bg-brand-hover text-black font-medium rounded-xl transition-all duration-200"
          >
            Go Back
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!survey) return null;

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Survey Responses</h1>
          <p className="text-gray-600 dark:text-gray-400">Survey ID: {surveyId}</p>
        </div>
        
        <div className="text-center py-12 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl">
          <p className="text-gray-600 dark:text-gray-400">Survey responses functionality coming soon...</p>
        </div>
      </div>
    </div>
  );
};

export default SurveyResponses; 